import { Button, Sidebar } from 'components/common';
import { Field, Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreatGroupeServices,
  GetUserslist,
  UpdateGroupeServices,
} from '../services/chatServices';

import UserShortCut from 'components/common/UserShortCut';
import { appendToFormData } from 'utils/globalMethodes';
import { toast } from 'react-toastify';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { CreateGroup, GroupDetail } from '../types/types';
import LucideIcon from 'components/common/LucideIcon';
import { ChatUserSelectSkeleton } from './ChatSkeletons';
import { useDebounce } from 'hooks/useDebounce';
import { useAuthContext } from 'contexts/AuthContext';
import * as Yup from 'yup';
import MintErrorMessage from 'components/common/MintErrorMessage';

interface CreateGroupeProps {
  setRefresh: Dispatch<SetStateAction<boolean>>;
  group?: GroupDetail;
}
const CreateGroupe: React.FC<SideBarProps<CreateGroupeProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const [usersList, setUsersList] = useState<SelectItem[] | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const [filterUserList, setFilterUserList] = useState({
    searchValue: '',
    lenght: 10,
    pageNumber: 0,
    groupId: params?.group?.id ?? 0,
  });
  const debouncedFilters = useDebounce(filterUserList, 300);
  const [preview, setPreview] = useState<string | null>(null);
  const { closeSidebar } = useSidebarManager();
  const { user } = useAuthContext();
  useEffect(() => {
    if (params.group && params.group?.id > 0) {
      setPreview(
        `/upload/profileimages/${params.group?.id}/${params.group?.imagePath}`,
      );
    }
    const fetchUsers = async (): Promise<void> => {
      const res = await GetUserslist(debouncedFilters);
      setUsersList((prev: SelectItem[] | undefined) => {
        if (prev === null || filterUserList.pageNumber === 0) {
          return res;
        }
        setIsLoading(false);
        return prev?.concat(res);
      });
    };
    fetchUsers();
  }, [debouncedFilters]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const threshold = 50;

    if (scrollPosition + threshold >= target.scrollHeight && !isLoading) {
      setIsLoading(true);
      setFilterUserList((prev) => ({
        ...prev,
        pageNumber: prev.pageNumber + 1,
      }));
    }
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  };

  const handelSelectUser = (
    userId: string,
    setFieldValue: (
      field: string,
      value: string[] | string | File | null,
      shouldValidate?: boolean,
    ) => Promise<void | FormikErrors<CreateGroup>>,
    values: CreateGroup,
    affectedType?: string,
  ): void => {
    let updatedUsersIds;
    if (affectedType == 'admin') {
      if (values.adminsIds.includes(userId)) {
        updatedUsersIds = values.adminsIds.filter(
          (id: string) => id !== userId,
        );
      } else {
        updatedUsersIds = [...values.adminsIds, userId];
      }
      setFieldValue('adminsIds', updatedUsersIds);
    } else {
      if (values.usersIds.includes(userId)) {
        updatedUsersIds = values.usersIds.filter((id: string) => id !== userId);
      } else {
        updatedUsersIds = [...values.usersIds, userId];
      }
      if (values.adminsIds.includes(userId)) {
        setFieldValue(
          'adminsIds',
          values.adminsIds.filter((id: string) => id !== userId),
        );
      }
      setFieldValue('usersIds', updatedUsersIds);
    }
  };

  const handelCreate = async (
    values: CreateGroup,
    { setSubmitting, setStatus }: FormikHelpers<CreateGroup>,
  ): Promise<void> => {
    try {
      setSubmitting(true);
      let response;
      if (params?.group?.id) {
        response = await UpdateGroupeServices(
          appendToFormData({
            ...values,
            usersIds: values.usersIds.join(','),
            adminsIds: values.adminsIds.join(','),
          }),
        );
      } else {
        response = await CreatGroupeServices(
          appendToFormData({
            ...values,
            usersIds: values.usersIds.join(','),
            adminsIds: values.adminsIds.join(','),
          }),
        );
      }
      if (response.success) {
        params.setRefresh((prev) => !prev);
        toast.success(t('Default.succesMessage'));
        closeSidebar(CreateGroupe);
      } else {
        setStatus(response.message);
        toast.error(response.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sidebar
      size="sm"
      type="secondary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
      className="tw-p-0 tw-overflow-hidden tw-top-0"
    >
      <Formik
        initialValues={
          {
            id: params?.group?.id ?? 0,
            title: params?.group?.name ?? '',
            description: params?.group?.description ?? '',
            usersIds: params?.group?.members.map((item) => item.id) ?? [],
            adminsIds: params?.group?.admins.map((item) => item.id) ?? [],
            members: [],
            admins: [],
            usersList: [],
            imageName: '',
            image: null,
            imageContent: [],
          } as CreateGroup
        }
        validationSchema={Yup.object({
          title: Yup.string().required(t('RequiredAttribute')),
          description: Yup.string().required(t('RequiredAttribute')),
          image: params.group?.id
            ? Yup.string().nullable()
            : Yup.string().required(t('Image.Required')),
        })}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handelCreate}
      >
        {({
          setFieldValue,
          values,
          errors,
          status,
          isSubmitting,
          submitCount,
        }) => (
          <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0 tw-bg-white">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-4 tw-pb-3">
              <h3 className="tw-text-[calc(var(--unit-minthr)*18)] tw-font-semibold tw-text-dark tw-m-0 tw-tracking-[-0.01em]">
                {values.id > 0 ? t('chat.Update.group') : t('chat.new.group')}
              </h3>

              <button
                onClick={() => {
                  closeSidebar(CreateGroupe);
                }}
                className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-border-none tw-bg-transparent tw-text-primary hover:tw-bg-light-gray2 tw-cursor-pointer tw-transition"
                type="button"
                aria-label={t('close', { defaultValue: 'Close' })}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>

            <Form className="tw-w-full tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden">
              <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-5 tw-pt-2 tw-pb-4">
                {status && (
                  <div className="tw-mb-4">
                    <MintErrorMessage message={status} />
                  </div>
                )}
                {!status && submitCount > 0 && errors.image && (
                  <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-danger-opacity">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="tw-text-danger tw-shrink-0"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    <span className="tw-text-xs tw-font-medium tw-text-danger">
                      {t(errors.image)}
                    </span>
                  </div>
                )}

                {/* Avatar + stacked inputs */}
                <div className="tw-flex tw-items-start tw-gap-4 tw-mb-2">
                  <div className="tw-relative tw-w-[calc(var(--unit-minthr)*60)] tw-h-[calc(var(--unit-minthr)*60)] tw-shrink-0">
                    <div className="tw-w-full tw-h-full tw-rounded-full tw-overflow-hidden tw-bg-primary-opacity tw-flex tw-items-center tw-justify-center">
                      {preview ? (
                        <img
                          id="preview-img"
                          className="tw-w-full tw-h-full tw-object-cover"
                          src={preview}
                          alt=""
                        />
                      ) : (
                        <img
                          id="empty-img"
                          className="tw-w-5 tw-h-5  tw-opacity-70"
                          src="/icons/image.svg"
                          alt=""
                        />
                      )}
                    </div>
                    <label className="tw-absolute tw--bottom-0.5 tw--right-0.5 tw-w-5 tw-h-5 tw-bg-primary tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer  tw-border-2 tw-border-white hover:tw-bg-primary-dark tw-transition">
                      <img
                        className="tw-w-3 tw-h-3 tw-icon-white "
                        id="upload-image"
                        src="/icons/plus.svg"
                        alt=""
                      />
                      <input
                        type="file"
                        name="image"
                        className="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files) {
                            setFieldValue('image', e.target.files[0]);
                            handleImageChange(e);
                          }
                        }}
                      />
                    </label>
                  </div>

                  <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-2">
                    <div className="tw-relative tw-w-full">
                      <Field
                        className="tw-w-full tw-bg-transparent tw-border-0 tw-border-b tw-border-gray3 tw-py-1.5 tw-text-[calc(var(--unit-minthr)*13)]  tw-text-dark tw-outline-none focus:tw-border-primary tw-transition"
                        placeholder={t('group.name.placeholder')}
                        name="title"
                      />
                      {submitCount > 0 && errors.title && (
                        <small className="tw-absolute tw-bottom-[-16px] tw-left-0 tw-text-xs tw-text-danger">
                          {t(errors.title)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="tw-relative tw-w-full tw-mb-5">
                  <Field
                    as="textarea"
                    rows={2}
                    className="tw-w-full tw-bg-transparent tw-border-0 tw-border-b tw-border-gray3 tw-py-1.5 tw-text-tw-text-[calc(var(--unit-minthr)*11)] tw-text-dark-gray3 tw-outline-none focus:tw-border-primary tw-transition tw-resize-none tw-min-h-[calc(var(--unit-minthr)*20)]"
                    placeholder={t('group.description.placeholder')}
                    name="description"
                  />
                  {submitCount > 0 && errors.description && (
                    <small className="tw-absolute tw-bottom-[-16px] tw-left-0 tw-text-xs tw-text-danger ">
                      {t(errors.description)}
                    </small>
                  )}
                </div>

                {/* Members count banner */}
                <div className="tw-flex tw-items-center tw-justify-between tw-bg-primary-opacity tw-rounded-lg tw-px-3.5 tw-py-2 tw-mb-3">
                  <h5 className="tw-text-[calc(var(--unit-minthr)*13)] tw-font-semibold tw-text-dark tw-m-0">
                    {t('Chat.group.members.added')}
                  </h5>
                  <div className="tw-text-[calc(var(--unit-minthr)*13)] tw-font-semibold">
                    <span className="tw-text-dark" id="members-count">
                      {values.usersIds.length + 1}
                    </span>
                    <span className="tw-text-dark-gray3">
                      /{usersList?.length}
                    </span>
                  </div>
                </div>

                {/* Search */}
                <div className="tw-w-full tw-mb-3">
                  <div className="tw-relative">
                    <div className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none tw-flex tw-items-center tw-z-10 tw-text-gray">
                      <LucideIcon
                        name="Search"
                        className="tw-w-3 tw-h-3 tw-icon-light-gray3"
                      />
                    </div>
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchText(value);
                        setFilterUserList((prev) => ({
                          ...prev,
                          searchValue: value,
                          pageNumber: 0,
                        }));
                      }}
                      placeholder={t('Search.Placeholder.title')}
                      className="tw-w-full tw-h-8 tw-rounded-md tw-border tw-border-gray3 tw-bg-[#f6f8f7] tw-pl-8 tw-pr-3 tw-text-[calc(var(--unit-minthr)*12.5)] tw-text-dark tw-outline-none focus:tw-border-primary tw-transition tw-placeholder:tw-text-dark-gray4"
                    />
                  </div>
                </div>

                {/* Users list */}
                <div
                  className="group-users-list tw-overflow-y-auto"
                  ref={listRef}
                  onScroll={handleScroll}
                >
                  {usersList === undefined && <ChatUserSelectSkeleton />}
                  {usersList &&
                    usersList.map((item, index) => {
                      const isMember =
                        values.usersIds.includes(item?.value ?? '') ||
                        user.userId == item?.value;
                      const isAdmin =
                        user.userId == item.value ||
                        values.adminsIds.includes(item.value ?? '--null--');

                      return (
                        <div
                          key={index}
                          className="tw-flex tw-items-center tw-gap-3 tw-px-1 tw-py-2 chat-user"
                        >
                          <UserShortCut
                            className="minthr-avatar tw-w-9 tw-h-9 tw-rounded-full tw-shrink-0"
                            src={`upload/profileimages/${item.value}/${item.imagePath}`}
                            alt=""
                          />
                          <div className="tw-min-w-0 tw-flex-1">
                            <h5 className="tw-text-sm tw-font-medium tw-text-dark tw-m-0 tw-truncate">
                              {item.text}
                            </h5>
                            <p className="tw-text-xs tw-text-dark-gray3 tw-m-0 tw-truncate">
                              {item.type}
                            </p>
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-2">
                            {isMember ? (
                              <>
                                <label
                                  className={`tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-cursor-pointer tw-transition ${
                                    isAdmin
                                      ? 'tw-bg-warning'
                                      : 'tw-bg-warning-opacity hover:tw-opacity-80'
                                  }`}
                                  onClick={() => {
                                    if (user.userId != item.value) {
                                      handelSelectUser(
                                        item.value as string,
                                        setFieldValue,
                                        values,
                                        'admin',
                                      );
                                    }
                                  }}
                                  htmlFor={`add-group-admin-${item.value}`}
                                  title={t('chat.admins')}
                                >
                                  <input
                                    type="checkbox"
                                    className="tw-hidden admin-checkbox"
                                    name={`add-group-admin-${item.value}`}
                                    checked={isAdmin}
                                    readOnly
                                  />
                                  <img
                                    className={`tw-w-3 tw-h-3 ${isAdmin ? 'tw-icon-white' : 'tw-icon-warning'}`}
                                    src="/icons/crown.svg"
                                    alt=""
                                  />
                                </label>

                                <label
                                  className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-cursor-pointer tw-transition tw-bg-light-gray3"
                                  onClick={() => {
                                    if (user.userId != item?.value) {
                                      handelSelectUser(
                                        item.value as string,
                                        setFieldValue,
                                        values,
                                      );
                                    }
                                  }}
                                  htmlFor={`add-group-member-${item.value}`}
                                  title={t('chat.members')}
                                >
                                  <input
                                    type="checkbox"
                                    className="tw-hidden member-checkbox"
                                    name={`add-group-member-${item.value}`}
                                    checked={isMember}
                                    readOnly
                                  />
                                  <img
                                    className="tw-w-2.5 tw-h-2.5 tw-icon-gray"
                                    src="/icons/check.svg"
                                    alt=""
                                  />
                                </label>
                              </>
                            ) : (
                              <label
                                className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-cursor-pointer tw-transition tw-bg-primary hover:tw-bg-primary-dark"
                                onClick={() => {
                                  if (user.userId != item?.value) {
                                    handelSelectUser(
                                      item.value as string,
                                      setFieldValue,
                                      values,
                                    );
                                  }
                                }}
                                htmlFor={`add-group-member-${item.value}`}
                                title={t('chat.members')}
                              >
                                <input
                                  type="checkbox"
                                  className="tw-hidden member-checkbox"
                                  name={`add-group-member-${item.value}`}
                                  checked={isMember}
                                  readOnly
                                />
                                <img
                                  className="tw-w-3 tw-h-3 tw-icon-white"
                                  src="/icons/plus.svg"
                                  alt=""
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Footer */}
              <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-5 tw-py-3 tw-border-t tw-border-gray3 tw-bg-white">
                <button
                  onClick={() => {
                    closeSidebar(CreateGroupe);
                  }}
                  type="button"
                  className=" tw-px-4 tw-py-1.5 tw-rounded-md tw-border tw-border-gray3 tw-bg-white tw-text-dark tw-text-tw-text-[calc(var(--unit-minthr)*13)] tw-font-semibold hover:tw-bg-light-gray2 tw-transition tw-cursor-pointer"
                >
                  {t('cancel')}
                </button>
                <Button
                  loading={isSubmitting}
                  type="submit"
                  className="tw-px-4 tw-py-1.5 tw-rounded-md tw-bg-primary hover:tw-bg-primary-dark tw-text-white tw-text-tw-text-[calc(var(--unit-minthr)*13)] tw-font-semibold tw-border-none tw-transition disabled:tw-opacity-60 test"
                >
                  {t('btn.save')}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </Sidebar>
  );
};

export default CreateGroupe;
